;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { zodResolver } from "@hookform/resolvers/zod";
import { uniq } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { GradientPseudoElement } from "@/components/layouts/GradientBorder";
import { PROVIDER_NAMES } from "@/components/settings/data-providers/constants";
import {
	PROVIDER_FORM_REGISTRY,
	useProviderFlow,
} from "@/components/settings/data-providers/provider-forms";
import { Button } from "@/components/ui/button";
import {
	Credenza,
	CredenzaContent,
	CredenzaDescription,
	CredenzaFooter,
	CredenzaHeader,
	CredenzaTitle,
} from "@/components/ui/credenza";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { HorizontalProgressStep } from "@/components/ui/progress-step/HorizontalProgress";
import { FormSelect } from "@/components/ui/select";
import { createCondition, WithCondition } from "@/components/ui/with-condition";
import { ProviderIcon } from "@/features/etl-automation/components/shared/provider-icon";
import { useOnboardingTours } from "@/features/onboarding-tours/context/use-onboarding-tours";
import { preventTourHostOutsideEvent } from "@/features/onboarding-tours/lib/tour-host-dismiss-guards";
import { TOUR_OVERLAY_GROUPS } from "@/features/onboarding-tours/orchestrator/tour-overlay-groups";
import { TOUR_TARGETS } from "@/features/onboarding-tours/orchestrator/tour-target-ids";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { useDataProviders } from "@/hooks/useDataProviders";
import { emitter, type ModalEvents } from "@/lib/emitter";
import {
	type CreateDataProviderSchema,
	createDataProviderSchema,
} from "@/lib/schema/data-provider";
import { hasWorkspacePermission } from "@/lib/utils/workspace-permissions";
import { ProviderType } from "@/types/dataProvider";

const ALL_PROVIDERS = Object.values(ProviderType).sort() as ProviderType[];

export function controlCreateDataProvider(
	properties: ModalEvents["settings.workspace.data_provider.create"],
) {
	emitter.emit("settings.workspace.data_provider.create", properties);
}

export function CreateDataProvider() {
	const { workspaceId, workspace } = useCurrentWorkspace();
	const { isTourActive, notifyTourHostDismissed } = useOnboardingTours();
	const [isOpen, setOpenState] = useState(false);
	const [isTourHostOpen, setTourHostOpen] = useState(false);
	const suppressTourAbandonRef = useRef(false);
	const allowTourHostCloseRef = useRef(false);
	const [preferredProvider, setPreferredProvider] = useState<
		ProviderType | undefined
	>(undefined);
	const [restrictToProviders, setRestrictToProviders] = useState<
		ProviderType[] | undefined
	>(undefined);
	const [
		hasAppliedInitialProviderSelection,
		setHasAppliedInitialProviderSelection,
	] = useState(false);

	const {
		createWorkspaceDataProvider,
		creatingWorkspaceDataProvider,
		pingWorkspaceDataProvider,
		pingingWorkspaceDataProvider,
		dataProviders,
	} = useDataProviders();

	const canCreateDataProvider = hasWorkspacePermission(
		workspace?.permissions,
		"dataProvider",
		"canCreate",
	);

	const availableProviders = useMemo(() => {
		const existingTypes = dataProviders.map((provider) => provider.type);

		if (restrictToProviders?.length) {
			return restrictToProviders.filter(
				(type) => !existingTypes.includes(type),
			);
		}

		const filteredProviders = ALL_PROVIDERS.filter(
			(type) => !existingTypes.includes(type),
		);

		return preferredProvider
			? uniq([preferredProvider, ...filteredProviders])
			: filteredProviders;
	}, [dataProviders, preferredProvider, restrictToProviders]);

	const providerOptions = useMemo(
		() =>
			availableProviders.map((type) => ({
				icon: <ProviderIcon className="size-5" providerId={type} size={20} />,
				label: PROVIDER_NAMES[type],
				value: type,
			})),
		[availableProviders],
	);

	const getInitialValues = useCallback(() => {
		const firstAvailable = availableProviders[0] || ProviderType.YARDI;
		return {
			data: {
				credentials: {
					apiKey: "",
					clientId: "",
					clientSecret: "",
					databaseName: "",
					developerId: "",
					email: "",
					password: "",
					personalAccessToken: "",
					superAdminEmail: "",
					workspaceGid: "",
					workspaceName: "",
				},
				projectIds: [] as string[],
				projectNames: [] as string[],
				type: firstAvailable,
			},
			workspaceId,
		} as CreateDataProviderSchema;
	}, [workspaceId, availableProviders]);

	const form = useForm<CreateDataProviderSchema>({
		defaultValues: getInitialValues(),
		resolver: zodResolver(createDataProviderSchema),
	});

	const selectedType = form.watch("data.type") as ProviderType;
	const isSingleRestrictedProvider =
		restrictToProviders?.length === 1 && Boolean(restrictToProviders[0]);

	const providerFlow = useProviderFlow({
		createWorkspaceDataProvider,
		creatingWorkspaceDataProvider,
		form,
		pingingWorkspaceDataProvider,
		pingWorkspaceDataProvider,
		selectedType,
	});
	const { initFlow } = providerFlow;

	const resetDialogState = useCallback(() => {
		setPreferredProvider(undefined);
		setRestrictToProviders(undefined);
		setHasAppliedInitialProviderSelection(false);
	}, []);

	const applyDialogClosedState = useCallback(() => {
		const wasTourHosted = isTourHostOpen;
		const suppressAbandon = suppressTourAbandonRef.current;

		form.reset(getInitialValues());
		providerFlow.resetFlow();
		resetDialogState();
		setTourHostOpen(false);
		setOpenState(false);
		allowTourHostCloseRef.current = false;

		if (wasTourHosted && !suppressAbandon) {
			notifyTourHostDismissed({
				overlayGroup: TOUR_OVERLAY_GROUPS.createDataProvider,
				reason: "complete",
			});
		}

		suppressTourAbandonRef.current = false;
	}, [
		form,
		getInitialValues,
		isTourHostOpen,
		notifyTourHostDismissed,
		providerFlow,
		resetDialogState,
	]);

	const requestDialogClose = useCallback(() => {
		allowTourHostCloseRef.current = true;
		controlCreateDataProvider({ state: "off" });
	}, []);

	useEffect(() => {
		if (!canCreateDataProvider) return;

		const handleCreateEvent = ({
			state,
			data,
			tourOrigin,
			reason,
		}: ModalEvents["settings.workspace.data_provider.create"]) => {
			if (state === "off") {
				if (reason === "tour-navigation") {
					suppressTourAbandonRef.current = true;
				}

				applyDialogClosedState();
				return;
			}

			setOpenState(true);
			if (tourOrigin) {
				setTourHostOpen(true);
			}
			setHasAppliedInitialProviderSelection(false);
			setPreferredProvider(
				data?.preferredProvider
					? (data.preferredProvider as ProviderType)
					: undefined,
			);
			setRestrictToProviders(
				data?.restrictToProviders
					? (data.restrictToProviders as ProviderType[])
					: undefined,
			);
		};

		emitter.on("settings.workspace.data_provider.create", handleCreateEvent);
		return () => {
			emitter.off("settings.workspace.data_provider.create", handleCreateEvent);
		};
	}, [applyDialogClosedState, canCreateDataProvider]);

	const markExplicitTourHostClose = () => {
		allowTourHostCloseRef.current = true;
	};

	const preventTourHostDismiss = (event: { preventDefault: () => void }) => {
		preventTourHostOutsideEvent({ event, isTourHostOpen });
	};

	const handleSubmitStep = async (data: CreateDataProviderSchema) => {
		const result = await providerFlow.handleStepSubmit(data);
		if (result?.success && providerFlow.isLastStep) {
			requestDialogClose();
		}
	};

	useEffect(() => {
		if (!isOpen) return;

		const targetProvider =
			restrictToProviders?.[0] ?? preferredProvider ?? availableProviders[0];

		if (!targetProvider) return;
		const isSingleRestrictedTarget = Boolean(
			restrictToProviders?.length === 1 && restrictToProviders[0],
		);

		if (!isSingleRestrictedTarget && hasAppliedInitialProviderSelection) {
			return;
		}

		const current = form.getValues("data.type") as ProviderType | undefined;
		if (current !== targetProvider) {
			form.setValue("data.type", targetProvider, {
				shouldDirty: false,
				shouldTouch: false,
				shouldValidate: false,
			});
		}
		if (!hasAppliedInitialProviderSelection) {
			setHasAppliedInitialProviderSelection(true);
		}
	}, [
		isOpen,
		hasAppliedInitialProviderSelection,
		preferredProvider,
		restrictToProviders,
		availableProviders,
		form,
	]);

	useEffect(() => {
		if (!isOpen) return;
		initFlow();
	}, [isOpen, initFlow]);

	useEffect(
		function exitTourHostModeWhenTourEnds() {
			if (!isTourActive && isTourHostOpen) {
				setTourHostOpen(false);
			}
		},
		[isTourActive, isTourHostOpen],
	);

	const ProviderForm = PROVIDER_FORM_REGISTRY[selectedType];
	const progressSteps = useMemo(
		() =>
			providerFlow.steps.map((step, stepIndex) =>
				stepIndex === providerFlow.currentStep
					? {
							...step,
							icon: (
								<ProviderIcon
									className="size-5"
									providerId={selectedType}
									size={20}
								/>
							),
						}
					: step,
			),
		[providerFlow.currentStep, providerFlow.steps, selectedType],
	);

	return (
		<Credenza
			onOpenChange={(open) => {
				if (open) {
					return;
				}

				if (isTourHostOpen && !allowTourHostCloseRef.current) {
					return;
				}

				requestDialogClose();
			}}
			open={isOpen}
		>
			<CredenzaContent
				className={
					isTourHostOpen ? "**:data-[slot=dialog-close-icon]:hidden" : undefined
				}
				id={TOUR_TARGETS.createDataProviderDialog}
				onEscapeKeyDown={() => {
					if (isTourHostOpen) {
						markExplicitTourHostClose();
					}
				}}
				onFocusOutside={preventTourHostDismiss}
				onInteractOutside={preventTourHostDismiss}
				onPointerDownOutside={preventTourHostDismiss}
			>
				<CredenzaHeader>
					<CredenzaTitle>Setup Data Provider</CredenzaTitle>
					<CredenzaDescription className="sr-only">
						Create a new provider integration
					</CredenzaDescription>
				</CredenzaHeader>
				<Form {...form}>
					<form
						className="w-full flex flex-col gap-5 px-4 sm:px-0"
						id="create-data-provider-form"
						onSubmit={form.handleSubmit(handleSubmitStep)}
					>
						<GradientPseudoElement className="rounded-lg">
							<HorizontalProgressStep
								className="**:data-[slot=step-content]:max-w-40 **:data-[slot=step-icon][data-status=current]:bg-background **:data-[slot=step-icon][data-status=current]:text-foreground"
								currentIndex={providerFlow.currentStep}
								steps={progressSteps}
							/>
						</GradientPseudoElement>
						<WithCondition
							condition={createCondition.truthy(providerFlow.currentStep === 0)}
						>
							{isSingleRestrictedProvider ? (
								<div
									className="space-y-2"
									id={TOUR_TARGETS.createDataProviderPicker}
								>
									<label
										className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
										htmlFor="restricted-data-provider"
									>
										Data Provider
									</label>
									<Input
										aria-label="Data Provider"
										disabled
										id="restricted-data-provider"
										readOnly
										value={
											PROVIDER_NAMES[
												restrictToProviders?.[0] as ProviderType
											] ?? "Google Admin"
										}
									/>
								</div>
							) : (
								<div id={TOUR_TARGETS.createDataProviderPicker}>
									<FormSelect
										disabled={providerFlow.isSubmitting}
										form={form}
										label="Data Provider"
										name="data.type"
										options={providerOptions}
										placeholder="Select a provider"
										triggerProps={{
											className: "w-full",
										}}
									/>
								</div>
							)}
						</WithCondition>

						{ProviderForm ? (
							<div
								className="space-y-5"
								id={TOUR_TARGETS.createDataProviderCredentialsSection}
							>
								<ProviderForm
									currentStep={providerFlow.currentStep}
									form={form}
									isCreating={providerFlow.isSubmitting}
									isEditMode={false}
									providerData={providerFlow.providerData}
								/>
							</div>
						) : null}
					</form>
				</Form>
				<CredenzaFooter>
					{providerFlow.currentStep > 0 ? (
						<Button
							disabled={providerFlow.isSubmitting}
							onClick={() => {
								const currentStep = providerFlow.currentStep;
								const previousStep = Math.max(0, currentStep - 1);
								if (previousStep === 0) {
									initFlow();
								}

								providerFlow.goToStep(previousStep);
							}}
							type="button"
							variant="outline"
						>
							Back
						</Button>
					) : (
						<Button
							disabled={providerFlow.isSubmitting}
							onClick={requestDialogClose}
							type="button"
							variant="outline"
						>
							Cancel
						</Button>
					)}

					<Button
						disabled={providerFlow.isSubmitting || !providerFlow.canSubmit}
						form="create-data-provider-form"
						id={TOUR_TARGETS.createDataProviderSubmit}
						type="submit"
					>
						{providerFlow.submitButtonText}
					</Button>
				</CredenzaFooter>
			</CredenzaContent>
		</Credenza>
	);
}
